import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { RiskBadge } from "../components/RiskBadge";
import DimensionCard from "../components/company/DimensionCard";
import SourceDialog from "../components/company/SourceDialog";
import LockedCompany from "../components/company/LockedCompany";
import { fetchCompany } from "../lib/api";

const DIMS = [
  { key: "customer", label: "Customer concentration", note: "Significant Customers note · revenue dependency", type: "pct" },
  { key: "geographic", label: "Geographic concentration", note: "Segment reporting · geographic revenue", type: "pct" },
  { key: "supplier", label: "Supplier concentration", note: "Risk Factors · supply chain dependency", type: "qual" },
  { key: "product", label: "Product concentration", note: "ASC 275 · disclosed dependence on a specific product or service", type: "pct" },
  { key: "credit", label: "Credit concentration", note: "ASC 825 · concentration of credit risk note", type: "pct" },
];

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
      <Footer />
    </div>
  );
}

export default function CompanyDetail() {
  const { ticker } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [src, setSrc] = useState(null); // {dim, label} for the source-verification modal

  useEffect(() => {
    setC(null); setErr(false);
    fetchCompany(ticker).then(setC).catch((e) => setErr(e?.response?.status === 401 ? "locked" : true));
  }, [ticker]);

  if (err === "locked") return <Shell><LockedCompany ticker={ticker} /></Shell>;
  if (err) return <Shell><div className="py-24 text-center text-neutral-500">Ticker "{ticker}" not found on SEC EDGAR, or no parsable 10-K/10-Q.</div></Shell>;
  if (!c) return <Shell><div className="py-24 text-center text-neutral-400">
    <p>Loading {ticker}…</p>
    <p className="mt-1 text-xs text-neutral-400">If this is a new company we're parsing its latest 10-K live from SEC EDGAR — this can take a few seconds.</p>
  </div></Shell>;

  return (
    <Shell>
      <div className="py-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#1a1a18]">{c.name}</h1>
              <RiskBadge level={c.composite_level} testid="company-composite-badge" />
            </div>
            <p className="mt-2 font-mono text-sm text-neutral-500">{c.ticker} · {c.exchange} · {c.sector} · CIK {c.cik}</p>
            <p className="mt-1 text-sm text-neutral-400">Latest filing: {c.customer.form_type} · {c.last_filing_date}</p>
          </div>
        </div>

        <div className="mt-10 space-y-6">
          {DIMS.map((d) => (
            <DimensionCard key={d.key} d={d} dim={c[d.key]}
              onViewSource={() => setSrc({ dim: d.key, label: d.label })} />
          ))}
        </div>

        <div className="mt-8 flex justify-end">
          <Link to={`/contact?about=${c.ticker}`} data-testid="report-correction-link"
            className="inline-flex items-center gap-1 rounded-full border border-[#e8e6df] bg-white px-3 py-1.5 text-xs text-neutral-500 hover:text-[#1a1a18] hover:border-neutral-400 transition-colors">
            Spot an error? Report a correction
          </Link>
        </div>

        <SourceDialog c={c} src={src} onClose={() => setSrc(null)} />
      </div>
    </Shell>
  );
}
